import type { List, Set } from '@gonfalon/immutable-shim/immutableShim';
import type { ImmutableMap } from '@gonfalon/legacy-immutable/immutableUtils';
import type { GetFeatureFlagsQueryParams } from '@gonfalon/openapi/serializeFlagListFilterParam';

import type { FilterKind, FilterValue } from '#components/ui/multipleFilters/types';

import type { AdditionalFilterOption } from './constants';
import type { CreationDateRange } from './creationDateFilter';
import type { evaluatedOptions } from './evaluatedOptions';

export enum FilterOperator {
  ARE_ALL_OF = 'are all of',
  IS = 'is',
  OVER = 'over',
}

export enum FlagListQueryParam {
  CREATION_DATE = 'creationDate',
  STATUS = 'status',
  TAG = 'tag',
  TARGETING = 'targeting',
  TYPE = 'type',
  CONTEXT_KIND_TARGETED = 'contextKindTargeted',
  CONTEXT_KINDS_EVALUATED = 'contextKindsEvaluated',
  SEGMENT_TARGETED = 'segmentTargeted',
  APPLICATION_EVALUATED = 'applicationEvaluated',
  STATE = 'state',
}

export enum FlagListBackendQueryParam {
  CREATION_DATE = 'creationDate',
  STATUS = 'status',
  TAGS = 'tags',
  TARGETING = 'targeting',
  TYPE = 'type',
  CONTEXT_KIND_TARGETED = 'contextKindTargeted',
  CONTEXT_KINDS_EVALUATED = 'contextKindsEvaluated',
  SEGMENT_TARGETED = 'segmentTargeted',
  APPLICATION_EVALUATED = 'applicationEvaluated',
  STATE = 'state',
}
export type EvaluatedDates = {
  'last-60-minutes': number;
  'last-24-hours': number;
  'last-7-days': number;
  'last-14-days': number;
  'last-30-days': number;
  'last-60-days': number;
};
export type FlagFiltersRecord = {
  tags: Set<string>;
  settings: List<NonNullable<NonNullable<GetFeatureFlagsQueryParams['filter']>['excludeSettings']>[number]>;
  sort: string;
  archived: boolean;
  offset: number;
  limit: number;
  q?: string;
  type?: NonNullable<GetFeatureFlagsQueryParams['filter']>['type'];
  maintainerId?: string;
  maintainerTeamKey?: string;
  hasExperiment?: boolean;
  hasDataExport?: boolean; // deprecated filter (v4 of the API)
  evaluated?: EvaluatedRanges;
  status?: string;
  sdkAvailability?: string;
  filterEnv?: string;
  targeting: string;
  codeReferences?: CodeReferencesFlagFilter;
  followerId?: string;
  creationDate?: CreationDateRange;
  contextKindTargeted?: string;
  segmentTargeted?: string;
  contextKindsEvaluated: Set<string>;
  applicationEvaluated?: string;
  state?: NonNullable<GetFeatureFlagsQueryParams['filter']>['state'];
  purpose?: 'holdout' | 'migration' | '!(holdout)' | '!(migration)' | 'all';
};
export type CodeReferencesFlagFilter = ImmutableMap<{
  min?: number;
  max?: number;
}>;
// `Partial` makes everything optional, and `Omit` allows us to re-specify a few props
export type FlagFilterProps = Omit<Partial<FlagFiltersRecord>, 'tags' | 'settings' | 'contextKindsEvaluated'> & {
  tags?: string[];
  settings?: Array<NonNullable<NonNullable<GetFeatureFlagsQueryParams['filter']>['excludeSettings']>[number]>;
  contextKindsEvaluated?: string[];
};
export type FlagFiltersQuery = Partial<{
  tag: string | string[];
  settings: string | string[];
  sort: string;
  archived: string;
  offset: string;
  limit: string;
  q: string;
  type: string;
  maintainerId: string;
  maintainerTeamKey: string;
  hasExperiment: string;
  hasDataExport: string; // deprecated filter (v4 of the API)
  evaluated: string;
  status: string;
  sdkAvailability: string;
  filterEnv: string;
  targeting: string;
  codeReferences: string;
  followerId: string;
  creationDate: string;
  contextKindTargeted: string;
  segmentTargeted: string;
  contextKindsEvaluated: string[];
  applicationEvaluated: string;
  state: string;
}>;
export type FlagFilterSelectOption = {
  value: FilterKind;
  label: string;
};
export type EvaluatedRanges = keyof typeof evaluatedOptions;
export type FilterDisplayType = FilterKind | AdditionalFilterOption;
export type FilterOptions = {
  [filterKeys: string]: string;
};
export type FlagFilterDataInterface = {
  operators: string[];
  options?: Array<{ label: string; value: FilterValue; filterOption: string }>;
  defaultValue?: FilterValue;
};
